<template>
  <div class="text-center">
    <v-dialog
      v-model="dialog"
      class="fraud-claim"
      width="1000px"
      scrollable
      persistent
    >
      <v-card>
        <v-card-title class="text-h5 grey lighten-2">
          Update Results
        </v-card-title>
        <v-card-text class="py-3">
          <v-row
            v-if="resultAlert && resultAlert.trim()"
            class="justify-center mb-3"
          >
            <v-col>
              <v-alert
                prominent
                dense
                border="start"
                colored-border
                color="warning"
                icon="mdi-history"
                class="mb-3"
                elevation="2"
              >
                <div class="d-flex align-center">
                  <div>
                    <div class="font-weight-bold text-subtitle-1 mb-1">
                      Previous Attempts Require Attention
                    </div>
                    <div class="text-body-2">
                      {{ resultAlert }}
                    </div>
                  </div>
                </div>
              </v-alert>
            </v-col>
          </v-row>
          <!-- Destinations List -->
          <v-row>
            <v-col>
              <h3 class="mb-2">
                Destination Status
              </h3>
              <v-list dense>
                <v-list-item
                  v-for="destination in destinationsList"
                  :key="destination.key || destination.name"
                  class="py-1"
                >
                  <template #prepend>
                    <v-icon
                      :color="getDestinationStatusColor(destination)"
                      size="20"
                    >
                      {{ getDestinationStatusIcon(destination) }}
                    </v-icon>
                  </template>

                  <v-list-item-title class="font-weight-bold">
                    {{ destination.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption">
                    {{ destination.details }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle
                    v-if="destination.errorMessage"
                    class="error--text text-caption"
                  >
                    <strong>Error:</strong> {{ destination.errorMessage }}
                  </v-list-item-subtitle>

                  <template #append>
                    <v-chip
                      size="x-small"
                      :color="getDestinationStatusColor(destination)"
                      :text-color="getDestinationStatusColor(destination) === 'warning'
                        ? 'black' : 'white'"
                    >
                      {{ getDestinationStatusText(destination) }}
                    </v-chip>
                  </template>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>

          <!-- Failure Resolution Options - Only show when there are failures -->
          <v-row
            v-if="fails.length > 0"
            class="mt-4"
          >
            <v-col cols="12">
              <v-divider class="mb-3" />
              <h3 class="mb-3 error--text">
                <v-icon
                  color="error"
                  class="mr-2"
                >
                  mdi-alert-circle
                </v-icon>
                Failure Resolution Options
              </h3>
            </v-col>

            <!-- Reverse Option -->
            <v-col :cols="canTryAlternateEmail ? 4 : 6">
              <v-card
                outlined
                class="h-100"
              >
                <v-card-title class="error--text py-2">
                  <v-icon
                    color="error"
                    class="mr-2"
                    size="20"
                  >
                    mdi-undo-variant
                  </v-icon>
                  Reverse Changes
                </v-card-title>
                <v-card-text class="py-2">
                  <p class="text-body-2 mb-2">
                    This will automatically reverse all successful updates that were made
                    before the failures occurred. Use this option when you want to
                    completely undo the entire operation.
                  </p>
                  <v-alert
                    dense
                    type="warning"
                    class="text-caption"
                  >
                    <span v-if="previousStatus !== 'REVERSE_FAILED'">
                      <strong>Warning:</strong> This action will undo all completed updates
                      and return all systems to their original state.
                    </span>
                    <span v-else>
                      <strong>Warning:</strong> This reverse action already failed previously, the
                      only option is to manually fix the failed and skipped systems.
                    </span>
                  </v-alert>
                </v-card-text>
                <v-card-actions class="pt-0 pb-2">
                  <v-spacer />
                  <v-btn
                    color="error"
                    small
                    :loading="reverseLoading"
                    :disabled="reverseDisabled"
                    @click="reverseChanges"
                  >
                    <v-icon
                      left
                      small
                    >
                      mdi-undo-variant
                    </v-icon>
                    Reverse All Changes
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>

            <!-- Manually Fixed Option -->
            <v-col :cols="canTryAlternateEmail ? 4 : 6">
              <v-card
                outlined
                class="h-100"
              >
                <v-card-title class="success--text py-2">
                  <v-icon
                    color="success"
                    class="mr-2"
                    size="20"
                  >
                    mdi-account-wrench
                  </v-icon>
                  Mark as Manually Fixed
                </v-card-title>
                <v-card-text class="py-2">
                  <p class="text-body-2 mb-2">
                    Use this option when you have manually fixed the failed
                    destinations outside of this system. This will only update
                    the ticket status and does not change the email address
                    in any destinations.
                  </p>
                  <v-alert
                    dense
                    type="info"
                    class="text-caption"
                  >
                    <strong>Note:</strong> This only updates the email update status.
                    Ensure you have manually updated the failed systems first.
                  </v-alert>
                </v-card-text>
                <v-card-actions class="pt-0 pb-2">
                  <v-spacer />
                  <v-btn
                    color="success"
                    small
                    :loading="manuallyFixedLoading"
                    :disabled="reverseLoading || canTryAlternateEmail"
                    @click="markAsManuallyFixed"
                  >
                    <v-icon
                      left
                      small
                    >
                      mdi-check-circle
                    </v-icon>
                    Mark as Fixed
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>

            <!-- Try Alternate Email Option -->
            <v-col
              v-if="canTryAlternateEmail"
              cols="4"
            >
              <v-card
                outlined
                class="h-100"
              >
                <v-card-title class="primary--text py-2">
                  <v-icon
                    color="primary"
                    class="mr-2"
                    size="20"
                  >
                    mdi-email-sync
                  </v-icon>
                  Try Alternate Email
                </v-card-title>
                <v-card-text class="py-2">
                  <p class="text-body-2 mb-2">
                    Use this option if you want to try a different email address.
                    Since no changes were made to any system, you can safely
                    go back and enter a new email.
                  </p>
                  <v-alert
                    dense
                    type="info"
                    class="text-caption"
                  >
                    <strong>Note:</strong> This will take you back to the initial screen
                    to try another email address.
                  </v-alert>
                </v-card-text>
                <v-card-actions class="pt-0 pb-2">
                  <v-spacer />
                  <v-btn
                    color="primary"
                    small
                    @click="$emit('tryAlternateEmail')"
                  >
                    <v-icon
                      left
                      small
                    >
                      mdi-email-sync
                    </v-icon>
                    Try Alternate
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider />

        <v-card-actions class="py-2">
          <v-spacer />
          <v-btn
            color="primary"
            :disabled="reverseLoading || manuallyFixedLoading"
            @click="handleClose()"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'UpdatePrimaryEmailResultDialog',
  props: {
    ticket: { type: Object, required: true },
    customer: { type: Object, required: true },
    completed: { type: Array, required: true },
    fails: { type: Array, required: true },
    destinations: { type: Array, required: true },
    modelValue: { type: Boolean, default: false },
    resultAlert: { type: String, default: '' },
    previousStatus: { type: [String, null], default: null },
  },
  data() {
    return {
      dialog: false,
      reverseLoading: false,
      manuallyFixedLoading: false,
      reverseAttemptsFailed: false,
    };
  },
  computed: {
    reverseDisabled() {
      if (this.manuallyFixedLoading) {
        return true;
      }

      if (this.previousStatus === 'REVERSE_FAILED') {
        return true;
      }

      if (this.reverseAttemptsFailed) {
        return true;
      }

      if (this.completed.length === 0) {
        return true;
      }

      return false;
    },
    destinationsList() {
      return this.destinations.map((destination) => {
        const isCompleted = this.isDestinationCompleted(destination);
        const isFailed = this.isDestinationFailed(destination);

        let status = 'skipped';

        if (isCompleted) {
          status = 'completed';
        }

        if (isFailed) {
          status = 'failed';
        }

        return {
          ...destination,
          status,
        };
      });
    },
    canTryAlternateEmail() {
      const loanpro = this.destinationsList.find((d) => d.key === 'loanpro');
      if (loanpro && loanpro.status === 'failed') {
        const otherThanLoanPro = this.destinationsList.filter((d) => d.key !== 'loanpro');
        const allOthersSkipped = otherThanLoanPro.every((d) => d.status === 'skipped');
        return allOthersSkipped;
      }
      return false;
    },
  },
  watch: {
    modelValue(val) {
      this.dialog = val;
    },
    dialog(val) {
      if (!val) {
        this.$emit('update:modelValue', false);
      }
    },
  },
  created() {
    this.dialog = this.modelValue;
  },
  methods: {
    ...mapActions('notification', ['setNotification']),
    ...mapActions('ticket', ['getTicket']),

    handleClose() {
      this.$emit('closeParentDialog');
      this.dialog = false;
    },

    async reverseChanges() {
      this.reverseLoading = true;

      try {
        const url = `api/v1/support/ticket/borrower/${this.customer.id * 1}/reverse-update-primary-email`;
        const payload = {
          ticketId: this.ticket.crm.id * 1,
          completedDestinations: this.destinationsList.filter((dest) => dest.status === 'completed'),
          failedDestinations: this.destinationsList.filter((dest) => dest.status === 'failed'),
        };

        const response = await this.$axios.post(url, payload);

        if (response.status === 200) {
          this.setNotification({
            message: 'All changes have been reversed.',
            color: 'success',
          });

          await this.getTicket(this.$route.params.id);

          this.$emit('closeParentDialog');
          this.dialog = false;
        } else {
          this.reverseAttemptsFailed = true;
        }
      } catch (error) {
        this.reverseAttemptsFailed = true;
        let errMsg = 'There was an error reversing the changes, manual intervention is required.';
        if (error.response && error.response.data && error.response.data.message) {
          errMsg = error.response.data.message;
        }
        this.setNotification({
          message: errMsg,
          color: 'red',
        });
      } finally {
        this.reverseLoading = false;
      }
    },

    async markAsManuallyFixed() {
      this.manuallyFixedLoading = true;

      try {
        const url = `api/v1/support/ticket/borrower/${this.customer.id * 1}/mark-primary-email-manually-fixed`;
        const payload = {
          ticketId: this.ticket.crm.id * 1,
          failedDestinations: this.destinationsList.filter((dest) => dest.status === 'failed'),
          completedDestinations: this.destinationsList.filter((dest) => dest.status === 'completed'),
        };

        const response = await this.$axios.post(url, payload);

        if (response.status === 200) {
          await this.setNotification({
            message: 'Update issue has been marked as manually fixed.',
            color: 'success',
          });

          await this.getTicket(this.$route.params.id);

          this.$emit('closeParentDialog');
          this.dialog = false;
        }
      } catch (error) {
        let errMsg = 'There was an error marking the ticket as manually fixed';
        if (error.response && error.response.data && error.response.data.message) {
          errMsg = error.response.data.message;
        }
        this.setNotification({
          message: errMsg,
          color: 'red',
        });
      } finally {
        this.manuallyFixedLoading = false;
      }
    },

    isDestinationCompleted(destination) {
      return this.completed.some((completedItem) => {
        if (typeof completedItem === 'string') {
          return completedItem === destination.name || completedItem === destination.key;
        }
        return completedItem.name === destination.name || completedItem.key === destination.key;
      });
    },

    isDestinationFailed(destination) {
      return this.fails.some((failedItem) => {
        if (typeof failedItem === 'string') {
          return failedItem === destination.name || failedItem === destination.key;
        }
        return failedItem.name === destination.name || failedItem.key === destination.key;
      });
    },

    getDestinationStatusColor(destination) {
      if (destination.status === 'completed') {
        return 'success';
      }
      if (destination.status === 'failed') {
        return 'error';
      }
      return 'warning'; // skipped
    },

    getDestinationStatusIcon(destination) {
      if (destination.status === 'completed') {
        return 'mdi-check-circle';
      }
      if (destination.status === 'failed') {
        return 'mdi-alert-circle';
      }
      return 'mdi-minus-circle';
    },

    getDestinationStatusText(destination) {
      if (destination.status === 'completed') {
        return destination.reversed ? 'Reversed' : 'Updated';
      }
      if (destination.status === 'failed') {
        return 'Failed';
      }
      return 'Skipped';
    },
  },
};
</script>
